import {
  ArrowLeft,
  Send,
  AlertCircle,
  Copy,
  RefreshCw,
  FileText,
  Check,
  CheckCircle,
} from 'lucide-react'
import { useState } from 'react'

import SupportTicketModal from '@/components/overlays/SupportTicketModal'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { usePaymentStatus } from '@/hooks/useCpn'
import { Payment, Transaction } from '@/types/payment'
import {
  getPaymentStatusInfo,
  getFailureReasonMessage,
  getPaymentMethodInfo,
  PAYMENT_REASON_LABELS,
} from '@/utils/cpn'
import { logger } from '@/utils/logger'
import {
  copyToClipboard,
  formatCurrency,
  formatDate,
  formatSettlementTime,
} from '@/utils/payment'

import { AmountInCad } from '../ui/CurrencyComponents'
import { TotalFeeInCad } from '../ui/TotalFeeInCad'

interface PaymentDetailsScreenProps {
  paymentId: string
  onBack: () => void
  cadUsdcRate?: number
}

const PaymentDetailsScreen = ({
  paymentId,
  onBack,
  cadUsdcRate,
}: PaymentDetailsScreenProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const {
    data: statusResponse,
    isLoading,
    error,
    refetch,
  } = usePaymentStatus(paymentId, !!paymentId)

  const payment: Payment | undefined = statusResponse?.payment
  const cadAmount: number | undefined = statusResponse?.cadAmount
  const transactions: Transaction[] = statusResponse?.transactions || []

  const handleCopy = async (text: string, fieldName: string) => {
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }

  // Handle refresh - refetch both payment status and swap status
  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      // Refetch payment status using TanStack Query's refetch
      await refetch()
    } catch (error) {
      logger.error('Failed to refresh data', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8  border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading payment details...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-[600px] flex flex-col bg-white dark:bg-gray-900">
        <div className="p-4  flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </Button>
          <h1 className="text-lg font-heading font-bold text-gray-900 dark:text-white">
            Payment Details
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="p-8 text-center max-w-md border-red-200 dark:border-red-800">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
              Failed to load payment
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Unable to retrieve payment details for ID: {paymentId}
            </p>
            <Button onClick={onBack} variant="outline">
              Go Back
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  // Check if payment and all transactions are completed
  const isPaymentCompleted = payment?.status.toUpperCase() === 'COMPLETED'
  const areTransactionsCompleted = transactions.every(
    (tx) => tx.status.toUpperCase() === 'COMPLETED'
  )
  const isCompleted = isPaymentCompleted && areTransactionsCompleted
  const hasIssue = payment && !isCompleted
  const statusInfo = getPaymentStatusInfo(payment?.status)

  return (
    <div className="h-[600px] flex flex-col overflow-hidden bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white hover:bg-white/20 p-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-heading font-bold">Payment Details</h1>
          </div>

          {/* Header Actions - Better aligned */}
          <div className="flex items-center space-x-1">
            {hasIssue && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSupportModal(true)}
                className="text-white hover:bg-white/20 px-3 py-2 flex items-center space-x-2"
                title="Open Support Ticket"
              >
                <FileText className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">Support</span>
              </Button>
            )}
            {/* Only show refresh button if payment is not completed or failed */}
            {!isCompleted && payment?.status.toUpperCase() !== 'FAILED' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="text-white hover:bg-white/20 px-3 py-2 flex items-center space-x-2"
                title="Refresh"
              >
                <RefreshCw
                  className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                <span className="text-sm hidden sm:inline">
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </span>
              </Button>
            )}
          </div>
        </div>

        {/* Status Overview - Enhanced */}
        {payment && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-md truncate">
                  {cadAmount && payment.fromCurrency === 'USDC' ? (
                    <>
                      {formatCurrency(cadAmount, 'CAD')} →{' '}
                      {formatCurrency(payment.toAmount, payment.toCurrency)}
                    </>
                  ) : (
                    <>
                      <AmountInCad
                        usdcAmount={payment.fromAmount}
                        originalCurrency={payment.fromCurrency}
                        cadUsdcRate={cadUsdcRate}
                        fallbackToHook={!cadUsdcRate}
                      />{' '}
                      → {formatCurrency(payment.toAmount, payment.toCurrency)}
                    </>
                  )}
                </p>
                <p className="text-blue-200 text-xs sm:text-sm">
                  Created {formatDate(payment.createdAt)}
                </p>
              </div>
              <div className="flex-shrink-0 self-start sm:self-auto">
                <span
                  className={`inline-flex items-center px-2 py-1 sm:px-3 sm:py-2 rounded-full text-xs sm:text-sm font-semibold ${
                    isCompleted
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                  }`}
                >
                  {statusInfo.label}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Content */}
      <div className="flex-1 overflow-y-auto rounded-none ">
        {/* Completion Notice - Show only for completed payments */}
        {isCompleted && (
          <div className="mx-4 mt-4 mb-0">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-green-800 dark:text-green-200 text-sm mb-1">
                    Transfer sent successfully
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-300 leading-relaxed">
                    Funds arrive within{' '}
                    {formatSettlementTime(
                      payment.fiatSettlementMin,
                      payment.fiatSettlementMax,
                      payment.fiatSettlementUnit
                    )}
                    . Need help? Email{' '}
                    <a
                      href="mailto:support@paytrie.com"
                      className="underline hover:no-underline font-medium"
                    >
                      support@paytrie.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Information */}
        {payment && (
          <Card className="p-6  bg-white dark:bg-gray-900 border-none">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <Send className="w-5 h-5 text-blue-600" />
              <span>Payment Information</span>
            </h3>

            {/* Always Visible Payment Details */}
            <div className="space-y-4 mb-6">
              {/* Fee and Settlement Time - Same Row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Total Fee (Provider + Platform) */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Fee
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      <TotalFeeInCad
                        providerFeeAmount={payment.feeAmount}
                        sendAmount={payment.fromAmount}
                        originalCurrency={payment.feeCurrency}
                        cadUsdcRate={cadUsdcRate}
                        fallbackToHook={!cadUsdcRate && !cadAmount}
                        cadAmount={cadAmount}
                      />
                    </span>
                  </div>
                </div>

                {/* Settlement Time */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Settlement Time
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {formatSettlementTime(
                        payment.fiatSettlementMin,
                        payment.fiatSettlementMax,
                        payment.fiatSettlementUnit
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Payment Method
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    {getPaymentMethodInfo(payment.paymentMethod).logo ? (
                      <img
                        src={getPaymentMethodInfo(payment.paymentMethod).logo}
                        alt={getPaymentMethodInfo(payment.paymentMethod).name}
                        className="w-5 h-5 object-contain"
                      />
                    ) : null}
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {getPaymentMethodInfo(payment.paymentMethod).name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Purpose - Full Width */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Payment Purpose
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {PAYMENT_REASON_LABELS[
                      payment.reasonForPayment as keyof typeof PAYMENT_REASON_LABELS
                    ] || payment.reasonForPayment}
                  </span>
                </div>
              </div>

              {/* Expiry - Only show if not completed and not failed */}
              {payment.expiresAt &&
                !isCompleted &&
                payment.status.toUpperCase() !== 'FAILED' && (
                  <div className="space-y-2 lg:col-span-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Expires At
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatDate(payment.expiresAt)}
                      </span>
                    </div>
                  </div>
                )}

              {/* Intent ID but we diplay to the user as Transaction ID */}
              <div className="space-y-2 lg:col-span-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Transaction ID
                </p>
                <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="flex-1 font-medium text-gray-900 dark:text-gray-100 font-mono text-sm break-all">
                    {paymentId}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(paymentId, 'intentId')}
                    className="flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Copy to clipboard"
                  >
                    {copiedField === 'intentId' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Failure Reason */}
              {payment.failureReason &&
                getFailureReasonMessage(payment.failureReason) && (
                  <div className="space-y-2 lg:col-span-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Issue Details
                    </p>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <span className="font-medium text-red-700 dark:text-red-400">
                        {getFailureReasonMessage(payment.failureReason)}
                      </span>
                    </div>
                  </div>
                )}
            </div>
          </Card>
        )}
        {/* Empty State */}
        {!payment && !isLoading && (
          <Card className="p-8 text-center border-gray-200 dark:border-gray-700">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="font-medium text-gray-900 dark:text-white mb-2">
              Payment not found
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              No payment details available for this transaction.
            </p>
            <Button onClick={onBack} variant="outline">
              Go Back
            </Button>
          </Card>
        )}
      </div>

      {/* Support Ticket Modal */}
      {showSupportModal && (
        <SupportTicketModal
          paymentId={paymentId}
          onClose={() => setShowSupportModal(false)}
        />
      )}

      {/* Footer */}
      <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            Secured by Paytrie • Circle Payments Network
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentDetailsScreen
